/**
 * The answers an approved plan gate recorded, for the plan on the record.
 *
 * Since answers edit the plan before approval (spec 2026-11-02, "The plan a
 * person approves is the only contract"), the approved version holds them as
 * its Decisions, and the approval's resolution carries them as it settled
 * them. This reads that record; nothing here infers an answer.
 */
import type { WorkItemGate } from "@yagni/shared";
import {
  readGatePlanQuestions,
  readPlanQuestionList,
  type PlanQuestionRow,
} from "../work/planQuestions.js";

/** One answer the approved plan gate recorded. */
export interface RecordedAnswer {
  questionId: string;
  answer: string;
  /** Reeve chose it (a skip, a stated decision, an Autonomous lane's judgment). */
  byReeve: boolean;
}

export interface ApprovedPlanAnswers {
  questions: PlanQuestionRow[];
  answers: RecordedAnswer[];
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * The answers on a resolution, in either shape the approval writes: the
 * legacy approval's `stagedAnswers` (`questionId`, `chosenBy`) or the lane's
 * `answers` (`id`, `chosen`, `attempts/answers.ts`). Total: anything else
 * reads as none.
 */
function readRecordedAnswers(resolution: Record<string, unknown>): RecordedAnswer[] {
  const out: RecordedAnswer[] = [];
  const legacy = Array.isArray(resolution.stagedAnswers) ? resolution.stagedAnswers : [];
  const lane = Array.isArray(resolution.answers) ? resolution.answers : [];
  for (const raw of [...legacy, ...lane]) {
    const entry = record(raw);
    if (!entry) continue;
    const id = typeof entry.questionId === "string" ? entry.questionId : entry.id;
    const answer = typeof entry.answer === "string" ? entry.answer.trim() : "";
    if (typeof id !== "string" || !id.trim() || !answer) continue;
    if (out.some((seen) => seen.questionId === id.trim())) continue;
    // The lane's `chosen` is Reeve's basis word ("judgment", "recommended"),
    // never an authorship flag: any non-empty one means Reeve chose it.
    const byReeve =
      entry.chosenBy === "reeve" || (typeof entry.chosen === "string" && entry.chosen !== "");
    out.push({ questionId: id.trim(), answer, byReeve });
  }
  return out;
}

/**
 * The answers the approved plan was approved with: the newest resolved plan
 * gate that approved, its questions off its own payload (the snapshot the
 * answers were keyed against), else the plan row's. A gate that names a plan
 * speaks only for that plan: when the sheet shows another (a newer plan than
 * the approved one), the old approval's answers are not drawn over it. Null
 * when no approval recorded an answer.
 */
export function approvedPlanAnswers(
  gates: readonly WorkItemGate[],
  planRow: { id?: unknown; questions?: unknown } | null,
): ApprovedPlanAnswers | null {
  let approved: WorkItemGate | null = null;
  for (const gate of gates) {
    if (gate.kind !== "approve_execution_plan" || gate.status !== "resolved") continue;
    if (record(gate.resolution)?.approved !== true) continue;
    if (!approved || (gate.resolvedAt ?? "") > (approved.resolvedAt ?? "")) approved = gate;
  }
  const resolution = record(approved?.resolution);
  if (!approved || !resolution) return null;
  if (approved.planId && approved.planId !== planRow?.id) return null;
  const answers = readRecordedAnswers(resolution);
  if (answers.length === 0) return null;
  const snapshot = readGatePlanQuestions(approved);
  const questions = snapshot.length > 0 ? snapshot : readPlanQuestionList(planRow?.questions);
  if (questions.length === 0) return null;
  return { questions, answers };
}
